import { useState, useRef, useMemo } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CodeIcon from "@mui/icons-material/Code";
import KeyIcon from "@mui/icons-material/Key";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import { API_ORIGIN, getToken } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { User } from "../lib/types";

interface OpenApiConnectProps {
  user: User;
}

export function OpenApiConnect({ user }: OpenApiConnectProps) {
  const { t, lang } = useI18n();
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);
  const [codeLang, setCodeLang] = useState<"curl" | "ts" | "python" | "go" | "php">("curl");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const iframeContainerRef = useRef<HTMLDivElement>(null);

  // Strict superadmin role check
  if (user.role !== "superadmin") {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", mt: 6, p: 3 }}>
        <Alert severity="error" icon={<LockOutlinedIcon />} sx={{ borderRadius: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t("openapi.deniedTitle")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t("openapi.deniedDesc")}
          </Typography>
        </Alert>
      </Box>
    );
  }

  const token = getToken() || "";
  const docsUrl = `${API_ORIGIN}/docs/index.html`;

  const specFiles = useMemo(() => {
    if (lang === "en") {
      return [
        {
          label: "🇻🇳 OpenAPI Spec (Vietnamese)",
          filename: "openapi-vi.yaml",
          url: `${API_ORIGIN}/docs/openapi-vi.yaml`,
          desc: "Full OpenAPI 3.0 specification with complete Vietnamese descriptions",
        },
        {
          label: "🇬🇧 OpenAPI Spec (English)",
          filename: "openapi-en.yaml",
          url: `${API_ORIGIN}/docs/openapi-en.yaml`,
          desc: "Full OpenAPI 3.0 specification with complete English descriptions and schemas",
        },
        {
          label: "🇨🇳 OpenAPI Spec (Chinese)",
          filename: "openapi-zh.yaml",
          url: `${API_ORIGIN}/docs/openapi-zh.yaml`,
          desc: "Full OpenAPI 3.0 specification with complete Chinese definitions and schemas",
        },
      ];
    }
    if (lang === "zh") {
      return [
        {
          label: "🇻🇳 OpenAPI 规范 (越南语)",
          filename: "openapi-vi.yaml",
          url: `${API_ORIGIN}/docs/openapi-vi.yaml`,
          desc: "OpenAPI 3.0 越南语完整接口定义文档与参数说明",
        },
        {
          label: "🇬🇧 OpenAPI 规范 (英语)",
          filename: "openapi-en.yaml",
          url: `${API_ORIGIN}/docs/openapi-en.yaml`,
          desc: "OpenAPI 3.0 英语完整接口定义文档与参数说明",
        },
        {
          label: "🇨🇳 OpenAPI 规范 (中文)",
          filename: "openapi-zh.yaml",
          url: `${API_ORIGIN}/docs/openapi-zh.yaml`,
          desc: "OpenAPI 3.0 规范，提供完整的中文接口定义与参数说明",
        },
      ];
    }
    return [
      {
        label: "🇻🇳 OpenAPI Spec (Tiếng Việt)",
        filename: "openapi-vi.yaml",
        url: `${API_ORIGIN}/docs/openapi-vi.yaml`,
        desc: "Đặc tả chuẩn OpenAPI 3.0 với mô tả tiếng Việt chi tiết cho toàn bộ endpoint",
      },
      {
        label: "🇬🇧 OpenAPI Spec (English)",
        filename: "openapi-en.yaml",
        url: `${API_ORIGIN}/docs/openapi-en.yaml`,
        desc: "Full OpenAPI 3.0 specification with complete English descriptions and schemas",
      },
      {
        label: "🇨🇳 OpenAPI Spec (中文)",
        filename: "openapi-zh.yaml",
        url: `${API_ORIGIN}/docs/openapi-zh.yaml`,
        desc: "OpenAPI 3.0 规范，提供完整的中文接口定义与参数说明",
      },
    ];
  }, [lang]);

  const postmanFiles = useMemo(() => {
    if (lang === "en") {
      return [
        {
          label: "🇻🇳 Postman Collection (Vietnamese)",
          filename: "postman_collection.json",
          url: `${API_ORIGIN}/docs/postman_collection.json`,
          desc: "Postman v2.1 Collection with Vietnamese requests and parameters",
        },
        {
          label: "🇬🇧 Postman Collection (English)",
          filename: "postman_collection_en.json",
          url: `${API_ORIGIN}/docs/postman_collection_en.json`,
          desc: "Postman v2.1 Collection with complete English requests and parameters",
        },
        {
          label: "🇨🇳 Postman Collection (Chinese)",
          filename: "postman_collection_zh.json",
          url: `${API_ORIGIN}/docs/postman_collection_zh.json`,
          desc: "Postman v2.1 Collection with complete Chinese requests and parameters",
        },
      ];
    }
    if (lang === "zh") {
      return [
        {
          label: "🇻🇳 Postman 集合文件 (越南语)",
          filename: "postman_collection.json",
          url: `${API_ORIGIN}/docs/postman_collection.json`,
          desc: "包含越南语描述的 Postman v2.1 集合文件",
        },
        {
          label: "🇬🇧 Postman 集合文件 (英语)",
          filename: "postman_collection_en.json",
          url: `${API_ORIGIN}/docs/postman_collection_en.json`,
          desc: "包含完整英语请求与参数的 Postman v2.1 集合文件",
        },
        {
          label: "🇨🇳 Postman 集合文件 (中文)",
          filename: "postman_collection_zh.json",
          url: `${API_ORIGIN}/docs/postman_collection_zh.json`,
          desc: "Postman v2.1 集合文件，包含完整的中文接口与参数配置",
        },
      ];
    }
    return [
      {
        label: "🇻🇳 Postman Collection (Tiếng Việt)",
        filename: "postman_collection.json",
        url: `${API_ORIGIN}/docs/postman_collection.json`,
        desc: "Collection v2.1 với request, header và mô tả tiếng Việt đầy đủ",
      },
      {
        label: "🇬🇧 Postman Collection (English)",
        filename: "postman_collection_en.json",
        url: `${API_ORIGIN}/docs/postman_collection_en.json`,
        desc: "Postman v2.1 Collection with complete English requests and parameters",
      },
      {
        label: "🇨🇳 Postman Collection (中文)",
        filename: "postman_collection_zh.json",
        url: `${API_ORIGIN}/docs/postman_collection_zh.json`,
        desc: "Postman v2.1 集合文件，包含完整的中文接口与参数配置",
      },
    ];
  }, [lang]);

  const handleCopy = (text: string, type: "token" | "snippet", snippetKey?: string) => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === "token") {
        setCopiedToken(true);
        setTimeout(() => setCopiedToken(false), 2000);
      } else if (snippetKey) {
        setCopiedSnippet(snippetKey);
        setTimeout(() => setCopiedSnippet(null), 2000);
      }
    });
  };

  const getSnippets = () => {
    const bearer = token ? token : "<YOUR_SUPERADMIN_JWT_TOKEN>";
    return {
      curl: `# 1. Get brand links by country and brand
curl -X GET "${API_ORIGIN}/api/brand-links?country=vn&brand=u888&limit=20" \\
  -H "Authorization: Bearer ${bearer}" \\
  -H "Accept: application/json"

# 2. Add a new brand link
curl -X POST "${API_ORIGIN}/api/brand-links" \\
  -H "Authorization: Bearer ${bearer}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "brand": "u888",
    "domain": "https://example-vip.com",
    "country": "vn"
  }'

# 3. Submit check results from mobile app (using Device Key)
curl -X POST "${API_ORIGIN}/api/device-api/checks" \\
  -H "X-Device-Key: dvk_sample_device_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "results": [
      { "linkId": 123, "status": "healthy", "duration": 185, "statusCode": 200 }
    ]
  }'`,

      ts: `import axios from 'axios';

const API_BASE = '${API_ORIGIN}/api';
const TOKEN = '${bearer}';

const client = axios.create({
  baseURL: API_BASE,
  headers: {
    Authorization: \`Bearer \${TOKEN}\`,
    'Content-Type': 'application/json',
  },
});

// 1. Fetch brand links
async function getLinks() {
  const { data } = await client.get('/brand-links', {
    params: { country: 'vn', brand: 'u888', limit: 50 },
  });
  console.log('Links:', data);
}

// 2. Create a new link
async function createLink(domain: string, brand: string) {
  const { data } = await client.post('/brand-links', {
    brand,
    domain,
    country: 'vn',
  });
  console.log('Created:', data);
}`,

      python: `import requests

API_BASE = "${API_ORIGIN}/api"
TOKEN = "${bearer}"

headers = {
    "Authorization": f"Bearer {TOKEN}",
    "Content-Type": "application/json",
    "Accept": "application/json"
}

# 1. Fetch brand links
resp = requests.get(
    f"{API_BASE}/brand-links",
    headers=headers,
    params={"country": "vn", "brand": "u888", "limit": 50}
)
print("Links:", resp.json())

# 2. Create a new link
new_link = requests.post(
    f"{API_BASE}/brand-links",
    headers=headers,
    json={"brand": "u888", "domain": "https://example-vip.com", "country": "vn"}
)
print("Created:", new_link.json())`,

      go: `package main

import (
	"bytes"
	"encoding/json"
	"fmt"
	"io"
	"net/http"
)

const (
	apiBase = "${API_ORIGIN}/api"
	token   = "${bearer}"
)

func main() {
	client := &http.Client{}

	req, err := http.NewRequest("GET", apiBase+"/brand-links?country=vn&brand=u888&limit=20", nil)
	if err != nil {
		panic(err)
	}
	req.Header.Set("Authorization", "Bearer "+token)
	req.Header.Set("Accept", "application/json")

	resp, err := client.Do(req)
	if err != nil {
		panic(err)
	}
	defer resp.Body.Close()

	body, _ := io.ReadAll(resp.Body)
	fmt.Println("Response:", string(body))
}`,

      php: `<?php

$apiBase = "${API_ORIGIN}/api";
$token = "${bearer}";

$ch = curl_init();
curl_setopt_array($ch, [
    CURLOPT_URL => "{$apiBase}/brand-links?country=vn&brand=u888&limit=20",
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        "Authorization: Bearer {$token}",
        "Accept: application/json"
    ],
]);

$response = curl_exec($ch);
curl_close($ch);

$data = json_decode($response, true);
print_r($data);`,
    };
  };

  const snippets = getSnippets();
  const currentSnippet = snippets[codeLang] ?? "";

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", pb: 6 }}>
      {/* Top Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 3,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))"
              : "linear-gradient(135deg, #eff6ff, #f8fafc)",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          sx={{
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 0.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  bgcolor: "warning.main",
                  color: "#fff",
                }}
              >
                <ElectricBoltIcon fontSize="small" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: -0.3 }}>
                {t("openapi.title")}
              </Typography>
              <Chip
                label={t("openapi.superadminBadge")}
                size="small"
                color="error"
                variant="filled"
                icon={<LockOutlinedIcon />}
                sx={{ fontWeight: 700, borderRadius: 1.5 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t("openapi.subtitle")}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(docsUrl, "_blank")}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {t("openapi.openNewTab")}
            </Button>
          </Stack>
        </Stack>

        {/* API Info Badges */}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ mt: 2.5, flexWrap: "wrap", gap: 1, alignItems: "center" }}
        >
          <Chip
            label={`Gateway Origin: ${API_ORIGIN}`}
            size="small"
            variant="outlined"
            sx={{ fontFamily: "monospace", fontWeight: 600 }}
          />
          <Chip
            label="API Base: /api"
            size="small"
            variant="outlined"
            sx={{ fontFamily: "monospace", fontWeight: 600 }}
          />
          <Chip
            label="Specification: OpenAPI 3.0.3"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      </Paper>

      {/* Grid: Auth Tokens & Specs */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, mb: 3 }}>
        {/* Auth Credentials Helper Card */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: "divider" }}>
          <CardContent sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1.2} sx={{ alignItems: "center", mb: 1.5 }}>
              <KeyIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t("openapi.authHeader")}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("openapi.authDesc")}
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 1.8,
                borderRadius: 2,
                bgcolor: (theme) => (theme.palette.mode === "dark" ? "action.hover" : "#f8fafc"),
                borderColor: "divider",
              }}
            >
              <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                  {t("openapi.activeToken")}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={copiedToken ? <CheckIcon /> : <ContentCopyIcon />}
                  color={copiedToken ? "success" : "primary"}
                  onClick={() => handleCopy(token, "token")}
                  sx={{ borderRadius: 1.5, py: 0.2, fontSize: "0.75rem", textTransform: "none" }}
                >
                  {copiedToken ? t("openapi.copied") : t("openapi.copyToken")}
                </Button>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.82rem",
                  wordBreak: "break-all",
                  bgcolor: (theme) => (theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f5f9"),
                  p: 1.2,
                  borderRadius: 1.5,
                  maxHeight: 70,
                  overflowY: "auto",
                  color: "primary.main",
                }}
              >
                {token || "No active token found"}
              </Typography>

              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                  {t("openapi.sampleHeaders")}
                </Typography>
                <Stack spacing={0.6} sx={{ mt: 0.6 }}>
                  <Box
                    component="code"
                    sx={{
                      fontSize: "0.78rem",
                      p: 0.8,
                      borderRadius: 1,
                      bgcolor: (theme) => (theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f5f9"),
                      display: "block",
                      fontFamily: "monospace",
                    }}
                  >
                    Authorization: Bearer {token ? `${token.slice(0, 24)}...` : "<token>"}
                  </Box>
                  <Box
                    component="code"
                    sx={{
                      fontSize: "0.78rem",
                      p: 0.8,
                      borderRadius: 1,
                      bgcolor: (theme) => (theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f5f9"),
                      display: "block",
                      fontFamily: "monospace",
                    }}
                  >
                    X-Device-Key: dvk_xxxxxxxxxxxxxxxx (Client App Mobile)
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </CardContent>
        </Card>

        {/* OpenAPI Spec Files Card */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: "divider" }}>
          <CardContent sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1.2} sx={{ alignItems: "center", mb: 1.5 }}>
              <FileDownloadIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t("openapi.specFiles")}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("openapi.specDesc")}
            </Typography>

            <Stack spacing={1.2}>
              {specFiles.map((file, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    bgcolor: (theme) => (theme.palette.mode === "dark" ? "action.hover" : "#fff"),
                    "&:hover": { borderColor: "primary.light" },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {file.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {file.filename} · {file.desc}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title={t("openapi.downloadYaml")}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => window.open(file.url, "_blank")}
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("openapi.copyUrl")}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(file.url, "snippet", `spec-${idx}`)}
                        color={copiedSnippet === `spec-${idx}` ? "success" : "default"}
                      >
                        {copiedSnippet === `spec-${idx}` ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Postman Collection & Integration Card */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: "divider", mb: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              mb: 1.5,
              gap: 1.5,
            }}
          >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: "#FF6C37",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: "1rem",
                }}
              >
                P
              </Box>
              <Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t("openapi.postmanTitle")}
                  </Typography>
                  <Chip
                    label="Collection v2.1 & OpenAPI 3.0"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t("openapi.postmanDesc")}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Quick instructions & Downloads */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.1fr 0.9fr" }, gap: 2.5, mt: 2 }}>
            {/* Left: 3 Postman Collection files */}
            <Stack spacing={1.2}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                POSTMAN COLLECTION (JSON):
              </Typography>
              {postmanFiles.map((file, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    bgcolor: (theme) => (theme.palette.mode === "dark" ? "action.hover" : "#fff"),
                    "&:hover": { borderColor: "warning.main" },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {file.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {file.filename} · {file.desc}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title={t("openapi.downloadJson")}>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => window.open(file.url, "_blank")}
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("openapi.copyPostmanUrl")}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(file.url, "snippet", `postman-${idx}`)}
                        color={copiedSnippet === `postman-${idx}` ? "success" : "default"}
                      >
                        {copiedSnippet === `postman-${idx}` ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            {/* Right: How to import guide */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => (theme.palette.mode === "dark" ? "action.hover" : "#f8fafc"),
                borderColor: "divider",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                {t("openapi.postmanGuideTitle")}
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1.2 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.8 }}>
                    <Chip label="1" size="small" sx={{ height: 20, width: 20, fontSize: "0.75rem", fontWeight: 700, bgcolor: "warning.main", color: "#fff" }} />
                    {t("openapi.postmanStep1Title")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.3, display: "block", mt: 0.3 }}>
                    {t("openapi.postmanStep1Text")}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.8 }}>
                    <Chip label="2" size="small" sx={{ height: 20, width: 20, fontSize: "0.75rem", fontWeight: 700, bgcolor: "warning.main", color: "#fff" }} />
                    {t("openapi.postmanStep2Title")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.3, display: "block", mt: 0.3 }}>
                    {t("openapi.postmanStep2Text")}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.8 }}>
                    <Chip label="3" size="small" sx={{ height: 20, width: 20, fontSize: "0.75rem", fontWeight: 700, bgcolor: "warning.main", color: "#fff" }} />
                    {t("openapi.postmanStep3Title")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 3.3, display: "block", mt: 0.3 }}>
                    {t("openapi.postmanStep3Text")}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </CardContent>
      </Card>

      {/* Code Integration Examples Card */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: "divider", mb: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              mb: 1.5,
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1.2} sx={{ alignItems: "center" }}>
              <CodeIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t("openapi.codeExamples")}
              </Typography>
            </Stack>

            <Tabs
              value={codeLang}
              onChange={(_, val) => val && setCodeLang(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5, px: 1.5, fontSize: "0.82rem" } }}
            >
              <Tab label="cURL" value="curl" />
              <Tab label="TypeScript / Node" value="ts" />
              <Tab label="Python" value="python" />
              <Tab label="Go" value="go" />
              <Tab label="PHP" value="php" />
            </Tabs>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              position: "relative",
              borderRadius: 2,
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "#1e1e1e" : "#1e293b"),
              color: "#f8fafc",
              p: 2,
              overflowX: "auto",
            }}
          >
            <Tooltip title={t("openapi.copySnippet")}>
              <IconButton
                size="small"
                onClick={() => handleCopy(currentSnippet, "snippet", "code-active")}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  color: "#cbd5e1",
                  bgcolor: "rgba(255,255,255,0.08)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
                }}
              >
                {copiedSnippet === "code-active" ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Box
              component="pre"
              sx={{
                m: 0,
                fontFamily: "monospace",
                fontSize: "0.85rem",
                lineHeight: 1.6,
                pr: 5,
              }}
            >
              {currentSnippet}
            </Box>
          </Paper>
        </CardContent>
      </Card>

      {/* Embedded Swagger UI Section */}
      <Card
        variant="outlined"
        ref={iframeContainerRef}
        sx={{
          borderRadius: 2.5,
          borderColor: "divider",
          overflow: "hidden",
          ...(isFullscreen
            ? {
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                borderRadius: 0,
                height: "100vh",
              }
            : {}),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.8,
            px: 2.5,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "action.hover" : "#f8fafc",
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: "success.main",
                boxShadow: "0 0 8px #22c55e",
              }}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Interactive Swagger UI Explorer
            </Typography>
            <Chip
              label="Live Sandbox"
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Tooltip title={t("openapi.reload")}>
              <IconButton size="small" onClick={() => setIframeKey((k) => k + 1)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isFullscreen ? t("openapi.exitFullscreen") : t("openapi.fullscreen")}>
              <IconButton size="small" onClick={() => setIsFullscreen(!isFullscreen)}>
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(docsUrl, "_blank")}
              sx={{ borderRadius: 1.5, textTransform: "none", fontSize: "0.8rem" }}
            >
              {t("openapi.openNewTab")}
            </Button>
          </Stack>
        </Box>

        <Box sx={{ width: "100%", height: isFullscreen ? "calc(100vh - 58px)" : "760px", bgcolor: "#fff" }}>
          <iframe
            key={iframeKey}
            src={docsUrl}
            title="OpenAPI Swagger UI"
            width="100%"
            height="100%"
            style={{ border: "none", display: "block" }}
          />
        </Box>
      </Card>
    </Box>
  );
}

export default OpenApiConnect;
